import React, { Component } from "react";
import { AiIcon } from "../AiIcon";
import { $, _ } from "../../../vendor";
import styled from "styled-components";
import { CanvasEventType } from "../../../../common/constants";
import AiGenerationService from "../../../core/services/aiGeneration";
import { themeColors } from "../../../react/sharedStyles";
import { Button } from "../../../Components/Button";
import { FlexSpacer } from "../../../react/components/Gap";
import { BeautifulDialog } from "../../../react/components/Dialogs/BaseDialog";
import { AiSlideContentEditor } from "../GeneratePresentation/Components/AiSlideContentEditor";
import { WithLabel } from "../../../Components/WithLabel";
import { FlexBox } from "../../../react/components/LayoutGrid";
import { HorizontalPropertyList, PropertyPanelContainer, PropertySection } from "../../../EditorComponents/PropertyPanel";
import { ToggleSwitch } from "../../../Components/ToggleSwitch";
import GeneratePresentationController from "../GeneratePresentation/GeneratePresentationController";
import { Dropdown } from "../../../Components/Dropdown";
import { MenuItem } from "../../../Components/Menu";
import { Icon } from "../../../Components/Icon";
import { Key } from "../../../core/utilities/keys";
import SlideAiController from "./SlideAiController";
import { AiImageSourcePopup, AiImageStylePopup } from "../GenerateImage/AiImageStyle";
import { DialogActions, DialogTitle } from "@material-ui/core";
import { SimpleLabel } from "../../../react/components/SimpleLabel";
import SlideAiIdeasList from "./SlideAiIdeasList";
import PresentationEditorController from "../../PresentationEditor/PresentationEditorController";
import { Slide } from "../../../core/models/slide";

const Pane = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    pointer-events: auto;
`;

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-areas: 
"prompt options"
"preview preview";
    grid-template-rows: 400px 1fr;
    grid-template-columns: 1fr 300px;
    background: #f9f9f9;
    row-gap: 10px;
`;

const Header = styled.div`
    height: 50px;
    background: #333;
    color: white;
    font-weight: bold;
    font-size: 18px;
    padding: 10px 20px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
`;

const PromptContainer = styled.div`
    grid-area: prompt;
    padding: 15px 20px 0px;
    display: grid;
    grid-template-rows: auto 1fr auto;
`;

const OptionsContainer = styled.div`
    grid-area: options;
    padding: 15px 20px 0px 0px;
    display: grid;
    grid-template-rows: auto 1fr;
`;

const PreviewContainer = styled.div`
    grid-area: preview;
    overflow: hidden;
`;

const TextContainer = styled.div`
    background: #fff;
    border: solid 1px #ccc;
    padding: 10px;
    width: 100%;
    //height: 385px;
    pointer-events: auto;
    overflow-y: auto;
`;

class GenerateNewSlidePane extends Component {
    constructor(props) {
        super(props);

        // create dummy slide for SLideAiController to use
        let dummySlide = new Slide({
            id: "--new--"
        }, {
            disconnected: true,
            autoLoad: false,
            presentation: props.presentation,
        });
        SlideAiController.setCurrentSlide(dummySlide);

        this.state = {
            currentSlide: dummySlide,
            isGenerating: false,
            slidePrompt: "list of 3 random flowers with their names and a brief description for each",
            slideType: "auto",
            preserveExactText: false,
            usePresentationAsContext: false,
            files: [],
            webSearch: false,
            isDragging: false,
            isSavingFiles: false,
            imageSource: "ai",
            imageStyle: null,
        };

        this.promptRef = React.createRef();
    }

    handleGenerateSlide = async () => {
        const { currentSlide, slideType, preserveExactText, usePresentationAsContext, files, imageSource, imageStyle, webSearch } = this.state;

        this.setState({ isGenerating: true });

        let slidePrompt = this.promptRef.current.getMarkdown();

        let slideImageResponse = await AiGenerationService.generateSlideImagePrompt(
            slidePrompt,
            usePresentationAsContext ? currentSlide.presentation.get("metadata")?.topic : null
        );
        if (slideImageResponse) {
            SlideAiController.regenerateSlideImage(slideImageResponse.slideImagePrompt, imageStyle);
        }

        await SlideAiController.regenerateIdeas(slidePrompt, {
            slideType,
            preserveExactText,
            usePresentationAsContext,
            files,
            webSearch
        });

        this.setState({ isGenerating: false });
    }

    handleSelectIdea = idea => {
        const { onGeneratedSlide } = this.props;
        onGeneratedSlide(idea);
        console.log(idea);
    }

    render() {
        const { currentTheme } = this.props;
        let { slidePrompt, imageSource, imageStyle, preserveExactText, usePresentationAsContext, slideType, isGenerating, webSearch } = this.state;

        return (
            <Pane>
                <Header><AiIcon contained size={20} />Generate New Slide</Header>

                <Container>
                    <PromptContainer>
                        <FlexBox left middle>
                            <SimpleLabel>SLIDE PROMPT</SimpleLabel>
                            <FlexSpacer />
                        </FlexBox>
                        <TextContainer>
                            <AiSlideContentEditor ref={this.promptRef} text={slidePrompt} onChange={slidePrompt => this.setState({ slidePrompt })} />
                        </TextContainer>
                        <FlexBox left middle gap={20}>
                            <WithLabel right tight label="Enable Web Search">
                                <ToggleSwitch value={webSearch} onChange={webSearch => this.setState({ webSearch })} />
                            </WithLabel>
                            <Button unframed><Icon>add</Icon>Add File...</Button>
                        </FlexBox>
                    </PromptContainer>
                    <OptionsContainer>
                        <FlexBox left middle>
                            <SimpleLabel>OPTIONS</SimpleLabel>
                        </FlexBox>
                        <PropertyPanelContainer fullHeight style={{ border: "solid 1px #ccc" }}>
                            <PropertySection>
                                <WithLabel label="Slide Type">
                                    <Dropdown value={slideType} onChange={slideType => this.setState({ slideType })}>
                                        <MenuItem value="auto">Auto</MenuItem>
                                        <MenuItem value="title">Title Slide</MenuItem>
                                        <MenuItem value="table">Table</MenuItem>
                                    </Dropdown>
                                </WithLabel>
                            </PropertySection>
                            <PropertySection>
                                <WithLabel left label="Use Presentation Context" toolTip="Generate this slide using the context of the entire presentation.">
                                    <ToggleSwitch value={usePresentationAsContext} onChange={usePresentationAsContext => this.setState({ usePresentationAsContext })} />
                                </WithLabel>
                            </PropertySection>
                            <PropertySection>
                                <WithLabel left label="Preserve Exact Text" toolTip="Try to keep the text in the generated slides exactly as it appears in the prompt.">
                                    <ToggleSwitch value={preserveExactText} onChange={preserveExactText => this.setState({ preserveExactText })} />
                                </WithLabel>
                            </PropertySection>
                            <PropertySection>
                                <HorizontalPropertyList>
                                    <AiImageSourcePopup imageSource={imageSource} onChange={imageSource => this.setState({ imageSource })} />
                                    {imageSource == "ai" && (
                                        <WithLabel label="Ai Image Style" above>
                                            <AiImageStylePopup theme={currentTheme} imageStyle={imageStyle} onChange={imageStyle => this.setState({ imageStyle })} />
                                        </WithLabel>
                                    )}
                                </HorizontalPropertyList>
                            </PropertySection>
                            <FlexSpacer />
                            <PropertySection fullWidth color={themeColors.lightBlue}>
                                <FlexBox fill>
                                    <Button
                                        large
                                        color={themeColors.aiColor}
                                        disabled={isGenerating}
                                        onClick={this.handleGenerateSlide}
                                        height={48}
                                        style={{ padding: "0 10px", margin: "10px 10px", width: "100%" }}
                                        showSpinner={isGenerating}
                                    >
                                        <Icon>
                                            <AiIcon />
                                        </Icon>
                                        Generate Slide
                                    </Button>
                                </FlexBox>
                            </PropertySection>
                        </PropertyPanelContainer>
                    </OptionsContainer>
                    <PreviewContainer>
                        <SlideAiIdeasList onSelect={this.handleSelectIdea} />
                    </PreviewContainer>
                </Container>
            </Pane>
        );
    }
}

export default PresentationEditorController.withState(GenerateNewSlidePane);
